import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { 
  BookOpen, 
  CreditCard, 
  Smartphone, 
  GraduationCap, 
  Globe, 
  Award, 
  Clock, 
  Star, 
  Calendar, 
  MessageCircle, 
  Video, 
  CheckCircle, 
  Zap, 
  Play, 
  AlertTriangle, 
  MapPin,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import { TutorProfile, pb } from '@/lib/pocketbase';
import TutorReviews from '@/components/TutorReviews';
import TutorAvailabilityCalendar from '@/components/TutorAvailabilityCalendar';
import { detectTextDirection, wrapTextWithBidiMarkers } from '@/utils/textDirection';
import LessonCard from '@/components/LessonCard';
import { useNavigate } from 'react-router-dom';
import { TutorPostsTab } from '@/components/TutorPostsTab';
import { convertToEmbeddableUrl } from '@/lib/utils';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';

interface TutorDetailsTabsProps {
  tutor: TutorProfile;
  localCoachInfo?: any;
  localMusicCoachInfo?: any;
  localTravelGuideInfo?: any;
  formatTutorPrice: (price: number) => string;
  getLocalizedText: (text: string) => string;
  formatAvailability: (availability: any) => string;
  getYouTubeEmbedUrl: (url: string) => string;
  responseTime: string;
  lessonsCount: number;
  lessons: any[];
  categorizeSubjects: (subjects: string[]) => { defaultOnes: string[]; approvedOnes: string[] };
  onBookSession: (type: 'regular' | 'coaching' | 'music_coaching' | 'travel_coaching') => void;
  onSendMessage: () => void;
}

const TutorDetailsTabs = ({
  tutor,
  localCoachInfo,
  localMusicCoachInfo,
  localTravelGuideInfo,
  formatTutorPrice,
  getLocalizedText,
  formatAvailability,
  getYouTubeEmbedUrl,
  responseTime,
  lessonsCount,
  lessons,
  categorizeSubjects,
  onBookSession,
  onSendMessage,
}: TutorDetailsTabsProps) => {
  const navigate = useNavigate();
  const { selectedCurrency } = useCurrencyFormat();
  const tutorName = tutor.name || tutor.expand?.user?.name || 'Unknown Tutor';
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showFullBio, setShowFullBio] = useState(false);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<Date>();

  // Helper function to sort levels in correct order: beginner → intermediate → advanced
  const sortLevelRates = (levelRates: Array<[string, unknown]>) => {
    const levelOrder: { [key: string]: number } = {
      'beginner': 1,
      'intermediate': 2,
      'advanced': 3
    };
    
    return levelRates.sort((a, b) => {
      const orderA = levelOrder[a[0].toLowerCase()] || 999;
      const orderB = levelOrder[b[0].toLowerCase()] || 999;
      return orderA - orderB;
    });
  };

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsListRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = direction === 'left' 
        ? tabsListRef.current.scrollLeft - scrollAmount
        : tabsListRef.current.scrollLeft + scrollAmount;
      
      tabsListRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Tabs defaultValue="posts" className="w-full">
      <div className="relative">
        {/* Left Arrow - Only on large desktop screens */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden xl:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-100"
          onClick={() => scrollTabs('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Right Arrow - Only on large desktop screens */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden xl:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-100"
          onClick={() => scrollTabs('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <div ref={tabsListRef} className="overflow-x-auto tabs-scroll-container">
          <TabsList className="w-full justify-start h-auto flex-nowrap rounded-none border-b bg-transparent p-0">
        <TabsTrigger value="posts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          <FileText className="h-4 w-4 mr-1" />
          Posts
        </TabsTrigger>
        <TabsTrigger value="lessons" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Lessons
        </TabsTrigger>
        <TabsTrigger value="about" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          About
        </TabsTrigger>
        <TabsTrigger value="availability" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Availability
        </TabsTrigger>
        <TabsTrigger value="pricing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Book
        </TabsTrigger>
        <TabsTrigger value="subjects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Subjects
        </TabsTrigger>
        <TabsTrigger value="video" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Videos
        </TabsTrigger>
        <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Reviews
        </TabsTrigger>
        <TabsTrigger value="payment" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Payment
        </TabsTrigger>
        <TabsTrigger value="experience" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Experience
        </TabsTrigger>
        <TabsTrigger value="facts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent whitespace-nowrap px-4 py-3">
          Quick Facts
        </TabsTrigger>
        </TabsList>
        </div>
      </div>

      {/* Posts Tab */}
      <TabsContent value="posts" className="mt-4">
        <Card>
          <CardContent className="p-2 md:p-4">
            <TutorPostsTab tutorId={tutor.id} />
          </CardContent>
        </Card>
      </TabsContent>

      {/* About Tab */}
      <TabsContent value="about" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="flex items-center text-sm md:text-base">
              <BookOpen className="h-3 w-3 md:h-4 md:w-4 mr-1.5 text-blue-600" />
              About {tutorName}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-4">
            <div className="prose max-w-none">
              <div className={`leading-loose text-xs md:text-sm ${!showFullBio ? 'line-clamp-6' : ''}`}>
                {(tutor.bio || 'This tutor has not provided a detailed bio yet. However, they are experienced in their field and ready to help you achieve your learning goals.')
                  .split('\n')
                  .map((paragraph, index) => {
                    const direction = detectTextDirection(paragraph);
                    const textAlign = direction === 'rtl' ? 'right' : 'left';
                    const textColor = direction === 'rtl' ? 'text-blue-700' : 'text-gray-700';
                    
                    return (
                      <p
                        key={index}
                        className={`mb-2 last:mb-0 ${textColor}`}
                        style={{
                          direction,
                          textAlign,
                          unicodeBidi: 'plaintext',
                        }}
                      >
                        {wrapTextWithBidiMarkers(paragraph)}
                      </p>
                    );
                  })}
              </div>
              {tutor.bio && tutor.bio.length > 150 && (
                <Button
                  variant="link"
                  className="mt-2 p-0 h-auto text-blue-600 text-xs"
                  onClick={() => setShowFullBio(!showFullBio)}
                >
                  {showFullBio ? 'See less' : 'See more'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Payment Methods Tab */}
      <TabsContent value="payment" className="mt-4">
        {tutor.paymentMethods && tutor.paymentMethods.length > 0 ? (
          <Card>
            <CardHeader className="p-2 md:p-4">
              <CardTitle className="flex items-center text-sm md:text-base">
                <CreditCard className="h-3 w-3 md:h-4 md:w-4 mr-1.5 text-green-600" />
                Payment Methods
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 md:p-4">
              <div className="flex flex-wrap gap-3">
                {tutor.paymentMethods.map((methodId) => {
                  const getPaymentMethodIcon = (methodId: string) => {
                    switch (methodId.toLowerCase()) {
                      case 'fawry':
                        return CreditCard;
                      case 'instapay':
                        return Smartphone;
                      default:
                        return CreditCard;
                    }
                  };

                  const getPaymentMethodName = (methodId: string) => {
                    switch (methodId.toLowerCase()) {
                      case 'fawry':
                        return 'Fawry';
                      case 'instapay':
                        return 'InstaPay';
                      default:
                        return methodId;
                    }
                  };

                  const PaymentIcon = getPaymentMethodIcon(methodId);
                  return (
                    <Badge
                      key={methodId}
                      variant="secondary"
                      className="bg-green-100 text-green-700 border border-green-200 text-lg py-2 px-4"
                    >
                      <PaymentIcon className="h-5 w-5 mr-2" />
                      {getPaymentMethodName(methodId)}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              No payment methods specified
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Experience & Education Tab */}
      <TabsContent value="experience" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="flex items-center text-sm md:text-base">
              <GraduationCap className="h-3 w-3 md:h-4 md:w-4 mr-1.5 text-blue-600" />
              Experience & Education
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 md:space-y-4 p-2 md:p-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Award className="h-4 w-4 mr-2 text-orange-500" />
                Teaching Experience
              </h4>
              <p className="text-gray-600 break-words">{tutor.experience || 'Professional teaching experience'}</p>
            </div>

            {tutor.education && Array.isArray(tutor.education) && tutor.education.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2 text-blue-500" />
                  Education
                </h4>
                <div className="space-y-3">
                  {tutor.education.map((edu: any, index: number) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="font-medium text-gray-900 break-words">{edu.degree}</div>
                      <div className="text-gray-600 break-words">{edu.institution}</div>
                      <div className="text-sm text-gray-500">Graduated: {edu.year}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Globe className="h-4 w-4 mr-2 text-green-500" />
                Languages
              </h4>
              <div className="flex flex-wrap gap-2">
                {tutor.languages && tutor.languages.length > 0 ? (
                  tutor.languages.map((language, index) => (
                    <Badge key={index} variant="outline" className="text-sm">
                      {language}
                    </Badge>
                  ))
                ) : (
                  <span className="text-gray-500">Languages not specified</span>
                )}
              </div>
            </div>

            {tutor.grades && tutor.grades.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2 text-purple-500" />
                  Grade Levels Taught
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tutor.grades.map((grade, index) => (
                    <Badge key={index} variant="outline" className="text-sm bg-purple-50 text-purple-700 border-purple-200">
                      {grade}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Local Coaching Information */}
        {localCoachInfo && (
          <Card className="border-green-200 bg-green-50 mt-4">
            <CardHeader>
              <CardTitle className="flex items-center text-lg text-green-800">
                <MapPin className="h-5 w-5 mr-2" />
                Local Fitness Coaching Available
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">In-Person Training</h4>
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified Local Coach
                  </Badge>
                </div>
                
                <div className="grid gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Location:</strong> {localCoachInfo.location?.city}, {localCoachInfo.location?.state}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Service Radius:</strong> {localCoachInfo.coverageRadius || 10} miles
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Local Rate:</strong> <span className="text-green-600 font-semibold">{formatTutorPrice(localCoachInfo.hourlyRate)}/{getLocalizedText("hour")}</span>
                    </p>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 mt-4"
                  onClick={() => onBookSession('coaching')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book Local Coaching Session
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Local Music Coaching */}
        {localMusicCoachInfo && (
          <Card className="border-purple-200 bg-purple-50 mt-4">
            <CardHeader>
              <CardTitle className="flex items-center text-lg text-purple-800">
                <MapPin className="h-5 w-5 mr-2" />
                Local Music Coaching Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">In-Person Music Lessons</h4>
                  <Badge className="bg-purple-100 text-purple-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified Local Music Coach
                  </Badge>
                </div>
                
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
                  onClick={() => onBookSession('music_coaching')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book Local Music Coaching Session
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Local Travel Guide */}
        {localTravelGuideInfo && (
          <Card className="border-blue-200 bg-blue-50 mt-4">
            <CardHeader>
              <CardTitle className="flex items-center text-lg text-blue-800">
                <MapPin className="h-5 w-5 mr-2" />
                Local Travel Guide Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">In-Person Travel Guidance</h4>
                  <Badge className="bg-blue-100 text-blue-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified Local Travel Guide
                  </Badge>
                </div>
                
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 mt-4"
                  onClick={() => onBookSession('travel_coaching')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book Local Travel Guide Session
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Subjects Tab */}
      <TabsContent value="subjects" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="flex items-center text-sm md:text-base">
              <BookOpen className="h-3 w-3 md:h-4 md:w-4 mr-1.5 text-blue-600" />
              Subjects & Expertise
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-4">
            {tutor.subjects && tutor.subjects.length > 0 ? (
              (() => {
                const { defaultOnes, approvedOnes } = categorizeSubjects(tutor.subjects);
                return (
                  <div className="space-y-6">
                    {/* Default subjects */}
                    {defaultOnes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 text-blue-500" />
                          Core Subjects
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {defaultOnes.map((subject, index) => (
                            <Badge key={index} variant="secondary" className="text-sm">
                              {subject}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Approved/specialized subjects */}
                    {approvedOnes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <AlertTriangle className="h-4 w-4 mr-2 text-green-600" />
                          Specialized Subjects
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {approvedOnes.map((subject, index) => (
                            <Badge key={index} variant="default" className="bg-green-100 text-green-800 border-green-300 text-sm">
                              {subject}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          These specialized subjects have been approved by our admin team based on the tutor&apos;s expertise.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <p className="text-gray-500">No subjects specified</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Availability Tab */}
      <TabsContent value="availability" className="mt-4">
        <TutorAvailabilityCalendar tutor={tutor} />
      </TabsContent>

      {/* Reviews Tab */}
      <TabsContent value="reviews" className="mt-4">
        <div className="p-2 md:p-0">
          <TutorReviews tutorId={tutor.id} canAddReview={true} />
        </div>
      </TabsContent>

      {/* Pricing & Booking Tab */}
      <TabsContent value="pricing" className="mt-4">
        <Card className="shadow-lg">
          <CardContent className="p-2 md:p-4">
            <div className="text-center mb-4">
              <div className="space-y-2">
                {/* Show ALL pricing options that have values */}
                {(() => {
                  const pricingOptions = [];
                  const isArabic = selectedCurrency.code === 'EGP';
                  
                  if (tutor.hourlyRate && tutor.hourlyRate > 0) {
                    const formattedPrice = formatTutorPrice(tutor.hourlyRate);
                    pricingOptions.push(
                      isArabic ? (
                        <div key="hourly" className="mb-2">
                          <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                            <span className="text-lg md:text-xl font-bold text-gray-900">
                              {formattedPrice.replace(selectedCurrency.symbol, '').trim()}
                            </span>
                            <span className="text-base md:text-lg font-semibold text-gray-900">{selectedCurrency.symbol}</span>
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">/ {getLocalizedText("hour")}</div>
                        </div>
                      ) : (
                        <div key="hourly" className="mb-2">
                          <div className="text-lg md:text-xl font-bold text-gray-900">
                            {formattedPrice}
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">{getLocalizedText("per hour")}</div>
                        </div>
                      )
                    );
                  }
                  
                  if (tutor.sessionRate && tutor.sessionRate > 0) {
                    const formattedPrice = formatTutorPrice(tutor.sessionRate);
                    pricingOptions.push(
                      isArabic ? (
                        <div key="session" className="mb-2">
                          <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                            <span className="text-lg md:text-xl font-bold text-green-600">
                              {formattedPrice.replace(selectedCurrency.symbol, '').trim()}
                            </span>
                            <span className="text-base md:text-lg font-semibold text-green-600">{selectedCurrency.symbol}</span>
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">/ جلسة</div>
                          <div className="text-gray-400 text-[8px] md:text-[10px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                        </div>
                      ) : (
                        <div key="session" className="mb-2">
                          <div className="text-lg md:text-xl font-bold text-green-600">
                            {formattedPrice}
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">{getLocalizedText("per session")}</div>
                          <div className="text-gray-400 text-[8px] md:text-[10px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                        </div>
                      )
                    );
                  }
                  
                  if (tutor.monthlyRate && tutor.monthlyRate > 0) {
                    const formattedPrice = formatTutorPrice(tutor.monthlyRate);
                    pricingOptions.push(
                      isArabic ? (
                        <div key="monthly" className="mb-2">
                          <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                            <span className="text-lg md:text-xl font-bold text-purple-600">
                              {formattedPrice.replace(selectedCurrency.symbol, '').trim()}
                            </span>
                            <span className="text-base md:text-lg font-semibold text-purple-600">{selectedCurrency.symbol}</span>
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">/ شهر</div>
                        </div>
                      ) : (
                        <div key="monthly" className="mb-2">
                          <div className="text-lg md:text-xl font-bold text-purple-600">
                            {formattedPrice}
                          </div>
                          <div className="text-gray-500 text-[10px] md:text-xs">{getLocalizedText("per month")}</div>
                        </div>
                      )
                    );
                  }
                  
                  if (tutor.levelRates && Object.keys(tutor.levelRates).length > 0) {
                    const validLevelRates = Object.entries(tutor.levelRates).filter(([level, rate]) => Number(rate) > 0);
                    if (validLevelRates.length > 0) {
                      const sortedLevelRates = sortLevelRates(validLevelRates);
                      pricingOptions.push(
                        <div key="level" className="mb-2 bg-orange-50 rounded-lg p-2 border border-orange-200">
                          <div className="text-[10px] md:text-xs font-medium text-orange-800 mb-1.5 text-center">{getLocalizedText("Pricing by Level")}</div>
                          <div className="space-y-1">
                            {sortedLevelRates.map(([level, rate]) => {
                              const formattedPrice = formatTutorPrice(Number(rate));
                              const isArabic = selectedCurrency.code === 'EGP';
                              return (
                                <div key={level} className="flex items-baseline justify-center gap-1 flex-wrap">
                                  <span className="capitalize font-medium text-gray-700 text-[10px] md:text-[10px]">{level}:</span>
                                  {isArabic ? (
                                    <>
                                      <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">{selectedCurrency.symbol}</span>
                                      <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">
                                        {formattedPrice.replace(selectedCurrency.symbol, '').trim()}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">{formattedPrice}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  if (pricingOptions.length === 0) {
                    return (
                      <div className="mb-2 text-center py-4">
                        <div className="text-lg font-bold text-gray-400">Contact for pricing</div>
                      </div>
                    );
                  }
                  
                  return pricingOptions;
                })()}
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <Button 
                size="sm" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs md:text-sm h-9"
                onClick={() => onBookSession('regular')}
              >
                <Calendar className="h-3 w-3 md:h-4 md:w-4 mr-1.5" />
                Book Session
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                className="w-full text-xs md:text-sm h-9"
                onClick={onSendMessage}
              >
                <MessageCircle className="h-3 w-3 md:h-4 md:w-4 mr-1.5" />
                Send Message
              </Button>
            </div>

            <Separator className="my-3" />

            <div className="space-y-2 text-[10px] md:text-xs">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Response time</span>
                <span className="font-medium">{responseTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Lessons taught</span>
                <span className="font-medium">{lessonsCount}+</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Introduction Video Tab */}
      <TabsContent value="video" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="flex items-center text-sm md:text-base">
              <Video className="h-3 w-3 md:h-4 md:w-4 mr-1.5 text-blue-600" />
              Introduction Video
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-4">
            {(() => {
              // Check for uploaded video file first
              const hasUploadedVideo = tutor.introVideoFile && tutor.introVideoFile.trim();
              const hasVideoUrl = tutor.introVideo && tutor.introVideo.trim();
              
              console.log('🎬 Video check:', { 
                hasUploadedVideo, 
                introVideoFile: tutor.introVideoFile,
                hasVideoUrl,
                introVideo: tutor.introVideo
              });
              
              if (hasUploadedVideo) {
                // Display uploaded video file
                const videoUrl = pb.files.getURL(tutor, tutor.introVideoFile);
                console.log('🎬 Generated video URL:', videoUrl);
                
                return (
                  <div className="space-y-2">
                    <div className="aspect-video rounded-lg overflow-hidden bg-gray-900 border-2 border-green-500">
                      <video
                        src={videoUrl}
                        className="w-full h-full object-contain"
                        controls
                        preload="metadata"
                        playsInline
                        onLoadedMetadata={() => console.log('🎬 Video loaded successfully')}
                        onError={(e) => {
                          console.error('🎬 Error loading video:', e);
                          const video = e.target as HTMLVideoElement;
                          if (video.error) {
                            console.error('🎬 Video error code:', video.error.code);
                            console.error('🎬 Video error message:', video.error.message);
                          }
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <p className="text-xs text-gray-500 text-center">Uploaded introduction video</p>
                  </div>
                );
              } else if (hasVideoUrl) {
                // Check if it's a Google Drive URL
                const isGoogleDrive = tutor.introVideo.includes('drive.google.com');
                const embedUrl = convertToEmbeddableUrl(tutor.introVideo);
                
                if (isGoogleDrive) {
                  // Google Drive: Show warning and direct link
                  return (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <p className="text-amber-800 font-medium mb-1">⚠️ Google Drive videos cannot be embedded</p>
                        <p className="text-amber-700">Click the button below to watch the introduction video directly.</p>
                      </div>
                      
                      <Button
                        variant="default"
                        size="lg"
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => window.open(embedUrl, '_blank')}
                      >
                        <Play className="h-5 w-5 mr-2" />
                        Watch Introduction Video
                      </Button>
                      
                      <p className="text-xs text-gray-500 text-center">
                        For embedded playback, please upload to YouTube or Vimeo
                      </p>
                    </div>
                  );
                } else {
                  // YouTube, Vimeo, or other embeddable videos: Show iframe
                  return (
                    <div className="aspect-video rounded-lg overflow-hidden bg-gray-900 relative">
                      <iframe
                        src={`${embedUrl}?modestbranding=1&rel=0&showinfo=0`}
                        title="Tutor Introduction Video"
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      {/* Top overlay to block channel name clicks */}
                      <div 
                        className="absolute top-0 left-0 right-0 h-14 cursor-default" 
                        onClick={(e) => e.preventDefault()}
                      />
                      {/* Bottom-right overlay to block YouTube logo */}
                      <div 
                        className="absolute bottom-0 right-0 w-24 h-10 cursor-default" 
                        onClick={(e) => e.preventDefault()}
                      />
                    </div>
                  );
                }
              } else {
                // No video available
                console.log('🎬 No video found');
                return (
                  <div className="bg-gray-100 rounded-lg p-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Play className="h-8 w-8 text-blue-600" />
                    </div>
                    <p className="text-gray-600 mb-4">
                      Introduction video coming soon
                    </p>
                    <Button variant="outline" size="sm" disabled>
                      <Play className="h-4 w-4 mr-2" />
                      Watch Video
                    </Button>
                  </div>
                );
              }
            })()}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Quick Facts Tab */}
      <TabsContent value="facts" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="text-sm md:text-base">Quick Facts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-2 md:p-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-gray-900">Verified Tutor</div>
                <div className="text-sm text-gray-500">Profile verified by our team</div>
              </div>
            </div>
            
            <div className="flex items-start">
              <Zap className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-gray-900">Quick Responses</div>
                <div className="text-sm text-gray-500">Usually replies within 2 hours</div>
              </div>
            </div>
            
            <div className="flex items-start">
              <Award className="h-5 w-5 text-purple-500 mr-3 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-gray-900">Top Rated</div>
                <div className="text-sm text-gray-500">Highly rated by students</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Lessons Tab */}
      <TabsContent value="lessons" className="mt-4">
        <Card>
          <CardHeader className="p-2 md:p-4">
            <CardTitle className="text-sm md:text-base">Lessons from {tutorName}</CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-4">
            {lessons && lessons.length > 0 ? (
              <div className="space-y-4">
                {lessons.slice(0, 3).map((lesson) => (
                  <div 
                    key={lesson.id}
                    onClick={() => navigate(`/lesson/${lesson.id}`)}
                    className="cursor-pointer"
                  >
                    <LessonCard
                      lesson={lesson}
                      tutorData={{
                        profile: tutor,
                        user: tutor?.expand?.user
                      }}
                      canEditLesson={() => false}
                      handleViewLesson={(lesson) => navigate(`/lesson/${lesson.id}`)}
                      handleEditLesson={() => {}}
                      handleDeleteLesson={() => {}}
                      handleJoinLesson={(lesson) => navigate(`/messages?tutor=${tutor?.id}&lesson=${lesson.id}`)}
                      handleBookSession={() => onBookSession('regular')}
                      formatDuration={(minutes) => {
                        const hours = Math.floor(minutes / 60);
                        const mins = minutes % 60;
                        if (hours > 0) {
                          return `${hours}h ${mins}m`;
                        }
                        return `${mins} min`;
                      }}
                      hasVideo={(lesson) => !!(lesson.videoFile || lesson.videoUrl)}
                      hasAttachment={(lesson) => !!lesson.attachment}
                      hasContent={(lesson) => !!lesson.content}
                    />
                  </div>
                ))}

                {lessons.length > 3 && (
                  <Button
                    onClick={() => navigate(`/tutor-lessons?tutorId=${tutor.id}`)}
                    variant="outline"
                    className="w-full"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    View All {lessons.length} Lessons
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">
                  No lessons available yet
                </h3>
                <p className="text-gray-500 text-sm">
                  This tutor hasn&apos;t created any lessons yet. Check back later!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default TutorDetailsTabs;
